import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import * as SwiperActions from '@planone/shared/data-access-swiper';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { ImageProviders } from '@planone/shared/util-pipelines';
import { debounceTime, of, switchMap, withLatestFrom } from 'rxjs';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class SwiperEffects {
  interaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SwiperActions.interaction),
      debounceTime(10),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      withLatestFrom(this.fairsFacade.fairId$),
      switchMap(([[interaction, exclude_analytics], fair_id]) => {
        const snowplowContext = [];

        if (interaction.exhibitor) {
          const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map(
            'EXHIBITOR',
            interaction.exhibitor
          );
          if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
        }

        if (interaction.event) {
          const snowplowEventContext = this.dataTransformationHelper.snowplow?.map('EVENT', interaction.event);
          if (snowplowEventContext) snowplowContext.push(snowplowEventContext);
        }

        if (interaction.product) {
          const snowplowProductContext = this.dataTransformationHelper.snowplow?.map(
            'PRODUCT',
            interaction.product,
            interaction.exhibitor?.fair_id
          );
          if (snowplowProductContext) snowplowContext.push(snowplowProductContext);
        }

        if (interaction.post) {
          const snowplowPostContext = this.dataTransformationHelper.snowplow?.map('POST', interaction.post);
          if (snowplowPostContext) snowplowContext.push(snowplowPostContext);
        }

        if (interaction.contact_person) {
          const snowplowContactPersonContext = this.dataTransformationHelper.snowplow?.map(
            'CONTACT_PERSON',
            interaction.contact_person,
            fair_id
          );
          if (snowplowContactPersonContext) snowplowContext.push(snowplowContactPersonContext);
        }

        return of(
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.gallery_impression,
              data: {
                type: interaction.interaction_type,
                image_resource: interaction.image_resource,
                image_provider: <ImageProviders>interaction.image_provider,
                image_description: interaction.image_description,
                image_position: interaction.image_position,
                context: interaction.context,
                page_type: interaction.page_type,
                exclude_analytics: exclude_analytics,
              },
            },
            context: snowplowContext ? snowplowContext : undefined,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'gallery_impression',
            image_description:
              interaction.image_position + '/' + interaction.image_count + ': ' + interaction.image_description,
            method: interaction.interaction_type,
            context: interaction.context,
            page_type: interaction.page_type,
            exclude_analytics: exclude_analytics,
            ...(interaction.exhibitor &&
              this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', interaction.exhibitor)),
            ...(interaction.product &&
              this.dataTransformationHelper.googleAnalytics?.map('PRODUCT', interaction.product)),
            ...(interaction.event && this.dataTransformationHelper.googleAnalytics?.map('EVENT', interaction.event)),
            ...(interaction.post && this.dataTransformationHelper.googleAnalytics?.map('POST', interaction.post)),
            ...(interaction.contact_person &&
              this.dataTransformationHelper.googleAnalytics?.map('CONTACT_PERSON', interaction.contact_person)),
          })
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private authenticationFacade: AuthenticationFacade,
    private fairsFacade: DataAccessFairsFacade,
    private dataTransformationHelper: DataTransformationHelper
  ) {}
}
